import {
  ChangeEvent,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  IconCheck,
  IconChevronDown,
  IconSearch,
  IconX,
} from '@tabler/icons-react';
import { twMerge } from 'tailwind-merge';

export type RichSelectOption = {
  label: string;
  value: string | number;
  /** Icon / visual displayed before the label (in the list and the field). */
  icon?: React.ReactNode;
  /** Renders the option as a colored pill (e.g. statuses). */
  pillClassName?: string;
  /** Displays the icon in the dropdown list only, not in the field once selected. */
  iconInMenuOnly?: boolean;
  description?: string;
};

interface RichSelectProps {
  options?: RichSelectOption[];
  value?: string | number | null;
  /** Label of the empty choice ("All groups"...). The empty choice is hidden when omitted. */
  placeholder?: string;
  /** Displays a search field filtering the options (autocomplete). Defaults to true. */
  searchable?: boolean;
  /** Icon displayed at the left of the field. */
  leftIcon?: React.ReactNode;
  loading?: boolean;
  disabled?: boolean;
  /** Message displayed when there are no options. */
  emptyMessage?: string;
  containerClassName?: string;
  triggerClassName?: string;
  menuWidth?: number;
  'aria-label'?: string;
  /**
   * Same signature as a native <select> onChange so that existing filter hooks
   * can be reused as is.
   */
  onChange?: (e: ChangeEvent<HTMLSelectElement>) => void;
  onValueChange?: (value: string) => void;
}

const normalize = (value: string) =>
  value.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

const OptionContent = ({ option }: { option: RichSelectOption }) =>
  option.pillClassName ? (
    <span
      className={twMerge(
        'inline-flex items-center gap-1.5 rounded-md border px-2 py-[3px] text-[10.5px] font-semibold uppercase tracking-wide leading-none',
        option.pillClassName,
      )}
    >
      {option.icon}
      {option.label}
    </span>
  ) : (
    <span className="flex min-w-0 items-center gap-2">
      {option.icon && (
        <span className="flex shrink-0 items-center">{option.icon}</span>
      )}
      <span className="truncate">{option.label}</span>
    </span>
  );

/**
 * Custom dropdown with optional autocomplete, icons and colored pills.
 * Drop-in replacement for <Select> in filters.
 */
export const RichSelect = ({
  options = [],
  value,
  placeholder,
  searchable = true,
  leftIcon,
  loading,
  disabled,
  emptyMessage,
  containerClassName,
  triggerClassName,
  menuWidth = 240,
  'aria-label': ariaLabel,
  onChange,
  onValueChange,
}: RichSelectProps) => {
  const { t } = useTranslation();
  const listId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlighted, setHighlighted] = useState(0);
  const [menuPosition, setMenuPosition] = useState<{
    top: number;
    left: number;
    width: number;
    maxHeight: number;
  }>();

  const selected = options.find(
    (option) => String(option.value) === String(value ?? ''),
  );

  const allOptions = useMemo<RichSelectOption[]>(
    () =>
      placeholder ? [{ label: placeholder, value: '' }, ...options] : options,
    [options, placeholder],
  );

  const filteredOptions = useMemo(() => {
    if (!query) return allOptions;
    const search = normalize(query);
    return allOptions.filter(
      (option) =>
        option.value !== '' && normalize(option.label).includes(search),
    );
  }, [allOptions, query]);

  const select = (option: RichSelectOption) => {
    const newValue = String(option.value);
    onChange?.({
      target: { value: newValue },
      currentTarget: { value: newValue },
    } as ChangeEvent<HTMLSelectElement>);
    onValueChange?.(newValue);
    setOpen(false);
    triggerRef.current?.focus();
  };

  const updatePosition = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const width = Math.max(rect.width, menuWidth ?? 0);
    const spaceBelow = window.innerHeight - rect.bottom - 12;
    const spaceAbove = rect.top - 12;
    const openAbove = spaceBelow < 220 && spaceAbove > spaceBelow;
    const maxHeight = Math.min(320, openAbove ? spaceAbove : spaceBelow);
    setMenuPosition({
      top: openAbove ? rect.top - 6 - maxHeight : rect.bottom + 6,
      left: Math.min(rect.left, window.innerWidth - width - 12),
      width,
      maxHeight,
    });
  };

  // Layout effects: the menu is positioned and its search field focused before
  // the next key press is handled, so nothing typed right after opening is lost.
  useLayoutEffect(() => {
    if (!open) return undefined;
    updatePosition();
    setQuery('');
    const index = allOptions.findIndex((option) => option === selected);
    setHighlighted(Math.max(0, index));

    const onClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        !triggerRef.current?.contains(target) &&
        !menuRef.current?.contains(target)
      ) {
        setOpen(false);
      }
    };
    const onScroll = (e: Event) => {
      if (!menuRef.current?.contains(e.target as Node)) updatePosition();
    };
    document.addEventListener('mousedown', onClickOutside);
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', onScroll, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Focuses the search field as soon as the menu is displayed (on the first
  // opening, the menu is only rendered once its position is computed).
  const isMenuDisplayed = open && Boolean(menuPosition);
  useLayoutEffect(() => {
    if (isMenuDisplayed) searchRef.current?.focus({ preventScroll: true });
  }, [isMenuDisplayed]);

  useEffect(() => {
    menuRef.current
      ?.querySelector(`[data-index="${highlighted}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [highlighted]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!open) {
      if (['ArrowDown', 'Enter', ' '].includes(e.key)) {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlighted((prev) => Math.min(prev + 1, filteredOptions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlighted((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const option = filteredOptions[highlighted];
      if (option) select(option);
    } else if (e.key === 'Escape') {
      setOpen(false);
      triggerRef.current?.focus();
    } else if (e.key === 'Tab') {
      setOpen(false);
    }
  };

  const isDisabled = disabled || loading;
  const hasValue = Boolean(selected) && String(value) !== '';

  return (
    <div className={twMerge('relative w-full', containerClassName)}>
      <button
        ref={triggerRef}
        type="button"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-label={ariaLabel}
        disabled={isDisabled}
        className={twMerge(
          'group flex h-[36px] w-full items-center gap-2 rounded-md border border-gray-300 bg-gray-FCFCFC px-2.5 text-left text-2sm text-gray-484848 transition-colors',
          'hover:border-gray-400 focus:outline-none focus-visible:border-purple-5433FF focus-visible:ring-2 focus-visible:ring-purple-5433FF/15',
          open && 'border-purple-5433FF ring-2 ring-purple-5433FF/15',
          hasValue && 'border-purple-5433FF/40 bg-purple-F7F5FF/60',
          isDisabled && 'cursor-not-allowed opacity-60',
          'dark-mode:bg-coal-500 dark-mode:border-gray-300 dark-mode:text-gray-700',
          triggerClassName,
        )}
        onClick={() => setOpen((prev) => !prev)}
        onKeyDown={onKeyDown}
      >
        {leftIcon && (
          <span className="flex shrink-0 items-center">{leftIcon}</span>
        )}
        <span className="flex min-w-0 flex-1 items-center">
          {loading ? (
            <span className="text-gray-500">{t('common:loading')}…</span>
          ) : selected && String(selected.value) !== '' ? (
            <OptionContent
              option={
                selected.iconInMenuOnly
                  ? { ...selected, icon: undefined }
                  : selected
              }
            />
          ) : (
            <span className="truncate text-gray-600">
              {placeholder ?? selected?.label}
            </span>
          )}
        </span>
        {loading ? (
          <span className="h-3.5 w-3.5 shrink-0 animate-spin rounded-full border-2 border-gray-400 border-t-transparent" />
        ) : hasValue && placeholder !== undefined && !disabled ? (
          <span
            role="button"
            tabIndex={-1}
            aria-label={t('common:clear')}
            className="flex shrink-0 items-center rounded p-0.5 text-gray-500 hover:bg-gray-200 hover:text-gray-800"
            onClick={(e) => {
              e.stopPropagation();
              select({ label: placeholder, value: '' });
            }}
          >
            <IconX size={14} />
          </span>
        ) : (
          <IconChevronDown
            size={16}
            className={twMerge(
              'shrink-0 text-gray-500 transition-transform',
              open && 'rotate-180',
            )}
          />
        )}
      </button>

      {open &&
        menuPosition &&
        createPortal(
          <div
            ref={menuRef}
            style={{
              top: menuPosition.top,
              left: menuPosition.left,
              width: menuPosition.width,
              maxHeight: menuPosition.maxHeight,
            }}
            className="fixed z-[1060] flex flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-[0_10px_30px_rgba(7,19,54,0.14)] dark-mode:bg-coal-600 dark-mode:border-gray-300"
            onKeyDown={onKeyDown}
          >
            {searchable && (
              <div className="flex items-center gap-2 border-b border-gray-100 px-2.5 py-2 dark-mode:border-gray-300">
                <IconSearch size={15} className="shrink-0 text-gray-400" />
                <input
                  ref={searchRef}
                  value={query}
                  placeholder={t('common:typeToSearch')}
                  className="w-full bg-transparent text-2sm outline-none placeholder:text-gray-400"
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setHighlighted(0);
                  }}
                />
              </div>
            )}
            <ul
              id={listId}
              role="listbox"
              className="flex-1 overflow-y-auto py-1"
            >
              {filteredOptions.length === 0 ? (
                <li className="px-3 py-2.5 text-2sm text-gray-500">
                  {query
                    ? t('common:noMatchingResult')
                    : (emptyMessage ?? t('common:noOption'))}
                </li>
              ) : (
                filteredOptions.map((option, index) => {
                  const isSelected =
                    String(option.value) === String(value ?? '');
                  return (
                    <li
                      key={`${option.value}-${index}`}
                      data-index={index}
                      role="option"
                      aria-selected={isSelected}
                      className={twMerge(
                        'mx-1 flex cursor-pointer items-center justify-between gap-2 rounded-md px-2.5 py-2 text-2sm text-gray-700 dark-mode:text-gray-800',
                        index === highlighted &&
                          'bg-purple-F7F5FF dark-mode:bg-coal-500',
                        isSelected && 'font-medium text-purple-5433FF',
                        option.value === '' && 'text-gray-500',
                      )}
                      onMouseEnter={() => setHighlighted(index)}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => select(option)}
                    >
                      <OptionContent option={option} />
                      {isSelected && (
                        <IconCheck
                          size={15}
                          className="shrink-0 text-purple-5433FF"
                        />
                      )}
                    </li>
                  );
                })
              )}
            </ul>
          </div>,
          document.body,
        )}
    </div>
  );
};
